import { Characteristic } from '@/services/models/Characteristic'
import { HPCharacteristic } from '@/services/models/HPCharacteristic'
import { DHWStorageCharacteristic } from '@/services/models/DHWStorageCharacteristic'
import { BufferTankCharacteristic } from '@/services/models/BufferTankCharacteristic'
import { BackupHeaterCharacteristic } from '@/services/models/BackupHeaterCharacteristic'
import { CoolingBufferTankCharacteristic } from '@/services/models/CoolingBufferTankCharacteristic'
import { AccessoryCharacteristic } from '@/services/models/AccessoryCharacteristic'
import { ECCharacteristic } from '@/services/models/ECCharacteristic'
import { SalesPackageCharacteristic } from '@/services/models/SalesPackageCharacteristic'
import { MountingMaterial } from '@/models/MountingMaterial.ts'
import { BuildingHull } from '@/models/BuildingHull.ts'
import { Article } from '@/services/models/Article.ts'

declare global {
  interface Window {
    dataLayer: Record<string, any>[]
    InstanaEumObject: 'ineum'
    ineum: {
      (tag: 'meta', identifier: string, value: string | number | boolean): void
      (tag: 'secrets', identifier: RegExp[]): void
      (tag: 'user', contactId: string): void
      (tag: 'page', url: string): void
      l?: number
      q?: string[][]
      v?: number
    }
    hj: any,
    _hjSettings: {
      hjid: number,
      hjsv: number
    },
    cmp_setlang: string
  }
}

export enum Environment {
  PRODUCTION = 'PROD',
  LOCALHOST = 'LOCAL',
  STAGE = 'STAGE',
}

export type StringMap = { [key: string]: any } // FIXME: Replace with Record
export type KeyNumType = { [key: string]: number } // FIXME: Replace with Record
export type EnergySourceType = {
  name: string
  value: string
  lowerRange: number
  upperRange: number
  cascadeMinPerformanceFactor: number
  cascadeMaxPerformanceFactor: number
  sourceTemperature: number
  maxFlowTemperature: number
}

export type GroundType = {
  name: string
  withdrawalPower: number
}

export type FormProcessInputType = any
export type FormProcessOutputType = any
export type FromValidationRuleType = string | string[] | undefined | StringMap | (() => Record<string, unknown>)

export type ProjectPropertyValueType<T = FormProcessInputType> = {
  val?: T
  formProcessType: FormProcessType
  formRevertType: FormProcessType
  step: HpStep
  resetsFromStep: HpStep
  required: boolean | (() => boolean)
  formValidationRule: FromValidationRuleType
  productDependency?: ProductType
}

export type ExistingBackupHeaterType = {
  name: string
  powerOutput?: number | string
  ebus: boolean
  dhw: boolean
}

export type ExistingDhwTankType = {
  name: string
  volume?: string
}

export type ProcessFunctionType = (val: FormProcessInputType) => FormProcessOutputType
export type RevertFunctionType = (val: FormProcessOutputType) => FormProcessInputType

export type FormPropertyType = Record<keyof ProjectPropertyType, FormProcessOutputType>

export type ProjectPropertyType = {
  name: ProjectPropertyValueType<string>
  firstName: ProjectPropertyValueType<string>
  lastName: ProjectPropertyValueType<string>
  emailAddress: ProjectPropertyValueType<string>
  phoneNumber: ProjectPropertyValueType<string>
  customerPostalCode: ProjectPropertyValueType<string>
  customerHouseNumber: ProjectPropertyValueType<string>
  customerStreet: ProjectPropertyValueType<string>
  customerCity: ProjectPropertyValueType<string>
  projectStreet: ProjectPropertyValueType<string>
  projectHouseNumber: ProjectPropertyValueType<string>
  projectPostalCode: ProjectPropertyValueType<string>
  projectCity: ProjectPropertyValueType<string>
  projectAltitude: ProjectPropertyValueType<number>
  heatingTemplateId: ProjectPropertyValueType<number>
  coolingTemplateId: ProjectPropertyValueType<number>
  applianceInterfaceId: ProjectPropertyValueType
  additionalProductId: ProjectPropertyValueType
  adjoinedBuildingSides: ProjectPropertyValueType<number>
  backupHeaterId: ProjectPropertyValueType
  powerOutputIntegratedBackupHeater: ProjectPropertyValueType<number>
  bufferTankId: ProjectPropertyValueType
  buildingHull: ProjectPropertyValueType<BuildingHull>
  circuits: ProjectPropertyValueType<Circuit[]>
  constructionYear: ProjectPropertyValueType<string>
  coolingBufferTankId: ProjectPropertyValueType
  coolingIndoorTemperature: ProjectPropertyValueType
  coolingLoad: ProjectPropertyValueType<number>
  coolingVentilationType: ProjectPropertyValueType
  previousFuelType: ProjectPropertyValueType<FuelType>
  operatingHours: ProjectPropertyValueType<number>
  annualConsumption: ProjectPropertyValueType<number>
  annualConsumptionUnit: ProjectPropertyValueType<string>
  dhwDemand: ProjectPropertyValueType
  dhwEnabled: ProjectPropertyValueType<boolean>
  dhwPersons: ProjectPropertyValueType
  dhwStorageId: ProjectPropertyValueType
  useExistingDhwTank: ProjectPropertyValueType
  explicitlyNoBufferTankSelected: ProjectPropertyValueType<boolean>
  explicitlyNoBackupHeaterSelected: ProjectPropertyValueType<boolean>
  explicitlyNoAccessoriesSelected: ProjectPropertyValueType<boolean>
  bufferTankInfoTexts: ProjectPropertyValueType<BufferTankInfoText[]>
  missingAccessoriesConfirmed: ProjectPropertyValueType<boolean>
  glazedSurfaces: ProjectPropertyValueType
  heatingIndoorTemperature: ProjectPropertyValueType<number>
  heatingLoss: ProjectPropertyValueType<number>
  livingArea: ProjectPropertyValueType<number>
  heatingVentilationType: ProjectPropertyValueType<string>
  heatPumpId: ProjectPropertyValueType
  heatPumpCascadeSize: ProjectPropertyValueType
  heatPumpPlacement: ProjectPropertyValueType<string>
  heatSource: ProjectPropertyValueType<EnergySource>
  groundType: ProjectPropertyValueType<string>
  groundTypesEnabled: ProjectPropertyValueType<boolean>
  hybrid: ProjectPropertyValueType<boolean>
  existingBackupHeater: ProjectPropertyValueType<ExistingBackupHeaterType>
  existingDhwTank: ProjectPropertyValueType<ExistingDhwTankType>
  installationTypes: ProjectPropertyValueType
  isolation: ProjectPropertyValueType
  selectedAccessories: ProjectPropertyValueType<Record<string, number>>
  systemControllerId: ProjectPropertyValueType<string>
  internetGatewayId: ProjectPropertyValueType<string>
  remoteControl: ProjectPropertyValueType<Article>
  controllerExtensions: ProjectPropertyValueType<Article[]>
  systemSchemeId: ProjectPropertyValueType<string>
  systemSchemeDocumentNumber: ProjectPropertyValueType<string>
  windowType: ProjectPropertyValueType<string>
  customArticles: ProjectPropertyValueType
  customPrices: ProjectPropertyValueType
  electricityPrice: ProjectPropertyValueType<number>
  comparisonFuelTypes: ProjectPropertyValueType<ComparisonFuelType[]>
  heatingCalculationMethod: ProjectPropertyValueType<HeatingCalculationMethod>
  coolingCalculationMethod: ProjectPropertyValueType<CoolingCalculationMethod>
  heatExchanger: ProjectPropertyValueType<boolean>
  bufferStrategy: ProjectPropertyValueType<BufferStrategy>
  mountingMaterial: ProjectPropertyValueType<MountingMaterial>
  hybridKit: ProjectPropertyValueType<boolean>
  salePackageId: ProjectPropertyValueType<string>
  salePackage: ProjectPropertyValueType<SalesPackageType>
  installerFirstName: ProjectPropertyValueType<string>
  installerLastName: ProjectPropertyValueType<string>
  installerPhoneNumber: ProjectPropertyValueType<string>
  installerEmailAddress: ProjectPropertyValueType<string>
  installerStreetName: ProjectPropertyValueType<string>
  installerHouseNumber: ProjectPropertyValueType<string>
  installerPostalCode: ProjectPropertyValueType<string>
  installerCity: ProjectPropertyValueType<string>
  installerCompanyName: ProjectPropertyValueType<string>
  installerCompanyNumber: ProjectPropertyValueType<string>
}

export type ComparisonFuelType = {
  fuelType: FuelType
  price: number
}

export type ProjectPropertyKeyType = keyof ProjectPropertyType

export interface CalorificValueConfig {
  unit: string
  calorificValue: number
}

export type ReferenceFuelType = {
  fuelType: FuelType
  heatingEfficiency: number
  emissionFactor: number
  calorificValues: CalorificValueConfig[]
  price: number
}

export type FormValType = {
  value: FormProcessOutputType
}

export type Characteristics =
  | Characteristic
  | HPCharacteristic
  | DHWStorageCharacteristic
  | ECCharacteristic
  | BufferTankCharacteristic
  | CoolingBufferTankCharacteristic
  | BackupHeaterCharacteristic
  | AccessoryCharacteristic
  | SalesPackageCharacteristic

export enum CharacteristicType {
  HEAT_PUMP = 'HEAT_PUMP',
  DHW_STORAGE = 'DHW_STORAGE',
  BUFFER_TANK = 'BUFFER_TANK',
  COOLING_BUFFER_TANK = 'COOLING_BUFFER_TANK',
  BACKUP_HEATER = 'BACKUP_HEATER',
  AIR_COLLECTOR = 'AIR_COLLECTOR',
  HYDRAULIC_MODULE = 'HYDRAULIC_MODULE',
  HYDRAULIC_TOWER = 'HYDRAULIC_TOWER',
  ACCESSORY = 'ACCESSORY',
  APPLIANCE_INTERFACE = 'APPLIANCE_INTERFACE',
  ELECTRICITY_CONSUMER = 'ELECTRICITY_CONSUMER',
  SALES_PACKAGE = 'SALES_PACKAGE',
  SYSTEM_CONTROLLER = 'SYSTEM_CONTROLLER',
  HEAT_EXCHANGER = 'HEAT_EXCHANGER',
  MOUNTING = 'MOUNTING',
  INTERNET_GATEWAY = 'INTERNET_GATEWAY',
  REMOTE_CONTROL = 'REMOTE_CONTROL',
  CONTROLLER_EXTENSION = 'CONTROLLER_EXTENSION',
}

export enum HPPlacement {
  FREE = 'FREE',
  ONE_WALL = 'ONE_WALL',
  TWO_WALLS = 'TWO_WALLS'
}

export enum InstallationType {
  HEATING = 'heating',
  COOLING = 'cooling'
}

export enum BufferStrategy {
  DECOUPLING = 'DECOUPLING',
  RETURN_FLOW = 'RETURN_FLOW'
}

export enum EnergySource {
  NONE = '',
  AIR = 'air',
  GROUND = 'ground',
  GROUND_VERTICAL = 'ground_vertical',
  GROUND_HORIZONTAL = 'ground_horizontal',
  WATER = 'water'
}

export enum ProductType {
  HEATPUMP = 'heatpump',
  DHWTANK = 'dhwtank',
  BUFFERTANK = 'buffertank',
  COOLING_BUFFERTANK = 'cooling_buffertank',
  BACKUPHEATER = 'backupheater',
  ACCESSORY = 'accessory',
  SYSTEM_CONTROLLER = 'controller',
  INTERNET_GATEWAY = 'internet_gateway',
  REMOTE_CONTROL = 'remote_control',
  CONTROLLER_EXTENSION = 'controller_extension',
  ADDITIONAL_PRODUCT = 'additional_product',
  SYSTEM_SCHEME = 'systemscheme',
  SALES_PACKAGE = 'salePackage'
}

export enum ProductIdType {
  DHWTANK = 'dhwStorageId',
  BUFFERTANK = 'bufferTankId',
  COOLING_BUFFERTANK = 'coolingBufferTankId',
  BACKUPHEATER = 'backupHeaterId',
  ADDITIONAL_PRODUCT = 'additionalProductId',
  APPLIANCE_INTERFACE = 'applianceInterfaceId'
}

export enum HpStep {
  EMPTY = '',
  PROJECT_DETAILS = 'StepProjectDetails',
  PLANNING = 'StepPlanning',
  HEAT_PUMP_SYSTEM = 'StepHeatPumpSystem',
  SALE_SET = 'StepSaleSet',
  COMPONENTS = 'StepComponents',
  INSTALLATION = 'StepInstallation',
  SYSTEM_CONTROLLERS = 'StepSystemControllers',
  SYSTEM_SCHEMES = 'StepSystemSchemes',
  ACCESSORIES = 'StepAccessories',
  SUMMARY = 'StepSummary'
}

export enum BusEvent {
  NO_OP = '',
  SHOW_MODAL = 'showModal',
  CLOSE_MODAL = 'closeModal',
  LOADING_STARTED = 'loadingStarted',
  LOADING_FINISHED = 'loadingFinished',
  KEYPRESSED_ESC = 'keypressed_esc',
  KEYPRESSED_ENTER = 'keypressed_enter',
  STEP_CHANGED = 'stepChanged',
  WINDOW_RESIZE = 'window_resize',
  REQUEST_SCHEME_CONFIRMED = 'requestSchemeConfirmed',
  FORCE_GO_TO_STEP = 'forceGoToStep',
  LOAD_PROJECT_FINISHED = 'loadProjectFinished',
  FOCUS_FIRST_ELEMENT = 'focusFirstElement',
  SCROLL_TO_FIRST_ERROR = 'scrollToFirstError',
  COPY_CONFIRMED = 'copyConfirmed',
  DELETE_CONFIRMED = 'deleteConfirmed',
  CONFIG_DELETE_CONFIRMED = 'configDeleteConfirmed',
  TRANSLATION_DELETE_CONFIRMED = 'translationDeleteConfirmed',
  CONFIG_DISABLE_CONFIRMED = 'configDisableConfirmed',
  CONFIG_KEY_CHANGE_WARNING = 'configKeyChangeWarning',
  LANGUAGE_CHANGED = 'languageChanged',
  UPDATE_PROJECT_CONFIRMED = 'updateProjectConfirmed',
  PROJECT_LIST_SEARCH_CHANGE = 'projectListSearchChange',
  USER_LOGOUT = 'userLogout',
  GO_TO_OVERVIEW = 'goToOverview'
}

export enum CircuitType {
  FAN_COIL = 'FAN_COIL',
  RADIATORS = 'RADIATORS',
  UNDERFLOOR = 'UNDERFLOOR'
}

export type Circuit = {
  systemType: CircuitType
  flowTemperature?: number
  cooling: boolean
}

export type FormCircuit = {
  id: string
  systemType?: CircuitType
  flowTemperature?: string
  cooling: boolean
}

export enum FlowTempsType {
  COOLING = 'coolingFlowTemperature',
  HEATING = 'heatingFlowTemperatures'
}

export enum IsolationType {
  WALLS = 'WALL',
  FLOOR = 'FLOOR',
  ROOF = 'ROOF'
}

export type GlazedSurface = {
  name: string
  orientation: string
  width: number
  height: number
  shaded: boolean
}

export type FormGlazedSurface = {
  id: string
  name: string
  orientation: string
  width: string
  height: string
  shaded: boolean
}

export type ButtonSetValueType = string | number | boolean

export type ButtonSetProps = {
  caption: string
  info?: string
  value: ButtonSetValueType
  iconIdentifier?: string
  iconUrl?: string
  disabled?: () => boolean
}

export enum FuelType {
  ELECTRICITY = 'ELECTRICITY',
  GAS = 'GAS',
  OIL = 'OIL',
  PELLET = 'PELLET',
  COAL = 'COAL'
}

export enum ValidationRuleType {
  REQUIRED = 'required',
  EMAIL = 'email',
  INTEGER = 'integer',
  MIN_VALUE = 'min_value',
  MAX_VALUE = 'max_value',
  ONE_OF = 'oneOf',
  MAX = 'max',
  POSITIVE_DECIMAL = 'positiveDecimal',
  POSITIVE_ZERO_DECIMAL = 'positiveZeroDecimal',
  DECIMAL = 'decimal',
  UNIQUE = 'unique',
  VALID_POSTAL_CODE = 'validPostalCode',
  TRANSLATION_KEY = 'translationKey',
  STARTS_WITH = 'startsWith'
}

export enum ConfigInputType {
  TOGGLE,
  TEXT,
  SELECT,
  OBJECT
}

export type ConfigSchemaItem = {
  type: ConfigInputType
  label: string
  description?: string
  hideLabel?: boolean
  key: string
  expandable?: boolean
  isUnique?: boolean
  options?: string[]
  items?: ConfigSchemaItem[]
  rules?: string | string[] | (() => Record<string, unknown>)
}

export type AvailableConfigsType = {
  brand: string
  country: string
}[]

export type AvailableTranslationType = {
  brand: string
  locale: string
}

export enum ProjectStatus {
  COMPLETE = 'COMPLETE',
  INCOMPLETE = 'INCOMPLETE',
  UPDATE_REQUIRED = 'UPDATE_REQUIRED'
}

export enum ProductStatus {
  ACTIVE = 'ACTIVE',
  PHASED_OUT = 'PHASED_OUT'
}

export enum SalesPackageType {
  SALES_PACKAGE = 'SALES_PACKAGE',
  BUNDLE = 'BUNDLE'
}

export type CustomArticleApiType = {
  id?: string
  name: string
  price: number
  customTotalPrice?: number
  quantity: number
  group: string
}

export type CustomAccessoryArticleType = {
  id: string
  name: string
  quantity: number
  orderNumber?: string
  pictureUrl?: string
  group?: string
  price: number
  productAdvantages?: string[]
}

export type AccessoryGroup = {
  group: string
}

export type CustomArticleType = {
  id: string
  name: string
  price?: number
  quantity: number
  group?: string
}

export type CustomPriceType = {
  productId: string
  price: number
}

export type CustomArticleGroupType = Record<string, CustomArticleType[]>

export enum FormProcessType {
  BASIC,
  BOOLEAN,
  INT,
  KEY_NUM,
  INTL_NUMBER,
  GLAZED_SURFACE,
  ISOLATION,
  CIRCUIT,
  ACCESSORY,
  ADDITIONAL_PRODUCT_ID,
  ENERGY_SOURCE,
  ARRAY,
  DECIMAL,
  OBJECT,
  BUILDING_HULL
}

export enum ReportVariant {
  CONSUMER = 'consumer',
  INSTALLER = 'installer',
  ELECTRIC_CONSUMPTION_CSV = 'electric_consumption_csv',
  BILLOFMATERIAL_CSV = 'bill_of_materials_csv',
  BILLOFMATERIAL_XLSX = 'bill_of_materials_xlsx'
}

export enum SortDirection {
  ASC = 'ASC',
  DESC = 'DESC'
}

export enum ModalResultType {
  CONFIRM,
  CANCEL,
  CLOSE
}

export type Category = {
  label: string
  count: number
}

export type NewsItem = {
  id?: string
  createdAt?: number
  localizedNews: LocalizedNewsItem[]
  country: string
  brand: string
}

export type LocalizedNewsItem = {
  language: string
  text: string
}

export enum NavItem {
  OVERVIEW = 'Overview',
  NEWS = 'News'
}

export enum HeatPumpStat {
  HEATING_PERFORMANCE = 'HEATING_PERFORMANCE',
  PEAK_HEAT_LOSS_COVERAGE = 'PEAK_HEAT_LOSS_COVERAGE',
  COOLING_PERFORMANCE = 'COOLING_PERFORMANCE',
  COOLING_LOAD_COVERAGE = 'COOLING_LOAD_COVERAGE',
  DHW_VOLUME = 'DHW_VOLUME',
  BIVALENCE_POINT = 'BIVALENCE_POINT',
  DISTANCE_DBA = 'DISTANCE_DBA',
  HEATING_OPERATION_POINT = 'HEATING_OPERATION_POINT',
  COOLING_OPERATION_POINT = 'COOLING_OPERATION_POINT',
  DRILLING_DEPTH = 'DRILLING_DEPTH'
}

export type HpBadge = {
  key: HeatPumpStat
  value: string
  description: string
  clickHandler?: () => void
}

export enum CoolingCalculationMethod {
  MANUAL = 'manual',
  THERMAL_ENVELOPE = 'thermal_envelope'
}

export enum HeatingCalculationMethod {
  MANUAL = 'manual',
  THERMAL_ENVELOPE = 'thermal_envelope',
  CONSUMPTION = 'consumption'
}

export enum RecommendationBadgeType {
  TOP = 'top',
  LEFT = 'left'
}

export enum DownloadExcelType {
  HEATPUMP_PERFORMANCE = 'calculationsexcel',
  BIVALENCE_POINT = 'bivalencepointexcel'
}

export enum BufferTankInfoText {
  TEXT_1 = 'TEXT_1',
  TEXT_2 = 'TEXT_2',
  TEXT_3 = 'TEXT_3',
  TEXT_4 = 'TEXT_4',
  TEXT_5a = 'TEXT_5a',
  TEXT_5b = 'TEXT_5b',
  TEXT_6 = 'TEXT_6'
}
